import { useState } from 'react';

import { askAssistant } from '../../lib/ask-ai.js';
import { Icon } from '../ui/icons.js';

/**
 * Make with Prisma: clothes, animations and props for the open model, in a
 * few taps. Each choice becomes a clear request to Prisma, who builds it into
 * the project (sewn garments with fabric materials, rig clips, prop models).
 */

const GARMENTS = ['T-shirt', 'Shirt', 'Hoodie', 'Sweater', 'Jacket', 'Coat', 'Dress', 'Skirt', 'Trousers', 'Jeans', 'Shorts', 'Cape', 'Scarf', 'Hat', 'Gloves', 'Boots', 'Sneakers'];
const FABRICS = ['Cotton', 'Denim', 'Wool', 'Knit', 'Leather', 'Satin', 'Canvas', 'Fleece'];
const PRINTS = ['None', 'Stripes', 'Plaid', 'Polka dots', 'Stars', 'Camo', 'Flowers', 'My own idea'];
const ANIMATIONS = ['Idle', 'Walk', 'Run', 'Jump', 'Wave', 'Dance', 'Sit', 'Attack', 'Cheer', 'Fall'];
const PROP_IDEAS = ['Treasure chest', 'Sword', 'Shield', 'Lantern', 'Potion bottle', 'Backpack', 'Crate', 'Street lamp', 'Bench', 'Magic staff'];

type Tab = 'outfit' | 'animation' | 'prop';

export function PrismaMakers({ modelPath }: { modelPath: string | null }) {
  const [tab, setTab] = useState<Tab>('outfit');
  const [garments, setGarments] = useState<string[]>(['T-shirt', 'Jeans']);
  const [fabric, setFabric] = useState('Cotton');
  const [color, setColor] = useState('#7c5cff');
  const [print, setPrint] = useState('None');
  const [printIdea, setPrintIdea] = useState('');
  const [anims, setAnims] = useState<string[]>(['Idle', 'Walk']);
  const [prop, setProp] = useState('');
  const [busy, setBusy] = useState(false);

  const toggle = (list: string[], set: (v: string[]) => void, item: string) => set(list.includes(item) ? list.filter((x) => x !== item) : [...list, item]);
  const target = modelPath ? `the character in ${modelPath}` : 'the character model that is open';

  async function go(text: string) {
    setBusy(true);
    await askAssistant(text);
    setBusy(false);
  }

  const outfit = () => {
    const printText = print === 'None' ? '' : print === 'My own idea' ? (printIdea.trim() ? ` with a printed pattern: ${printIdea.trim()} (make the print with prism_image_generate)` : '') : ` with a ${print.toLowerCase()} pattern`;
    return go(
      `Dress ${target} in: ${garments.join(', ')}. Main fabric ${fabric.toLowerCase()} in the colour ${color}${printText}. Make each piece a sewn garment fitted to the body (layered in the right order), give it a matching fabric material, and render it so I can see it.`,
    );
  };

  return (
    <div className="makers">
      <div className="makers-tabs" role="tablist" aria-label="Make with Prisma">
        {(
          [
            ['outfit', 'Outfits'],
            ['animation', 'Animations'],
            ['prop', 'Props'],
          ] as const
        ).map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={tab === id} className={tab === id ? 'is-on' : ''} onClick={() => setTab(id)}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'outfit' && (
        <div className="makers-body">
          <div className="makers-chips">
            {GARMENTS.map((g) => (
              <button key={g} type="button" className={garments.includes(g) ? 'is-on' : ''} onClick={() => toggle(garments, setGarments, g)}>
                {g}
              </button>
            ))}
          </div>
          <div className="makers-row">
            <label>
              Fabric
              <select value={fabric} onChange={(e) => setFabric(e.target.value)}>
                {FABRICS.map((f) => (
                  <option key={f}>{f}</option>
                ))}
              </select>
            </label>
            <label>
              Colour
              <input type="color" value={color} onChange={(e) => setColor(e.target.value)} />
            </label>
            <label>
              Pattern
              <select value={print} onChange={(e) => setPrint(e.target.value)}>
                {PRINTS.map((p) => (
                  <option key={p}>{p}</option>
                ))}
              </select>
            </label>
            {print === 'My own idea' && <input className="makers-idea" value={printIdea} onChange={(e) => setPrintIdea(e.target.value)} placeholder="e.g. tiny rocket ships" />}
          </div>
          <button type="button" className="makers-go" disabled={busy || garments.length === 0} onClick={() => void outfit()}>
            <Icon name="sparkles" size={13} /> Make this outfit
          </button>
        </div>
      )}

      {tab === 'animation' && (
        <div className="makers-body">
          <div className="makers-chips">
            {ANIMATIONS.map((a) => (
              <button key={a} type="button" className={anims.includes(a) ? 'is-on' : ''} onClick={() => toggle(anims, setAnims, a)}>
                {a}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="makers-go"
            disabled={busy || anims.length === 0}
            onClick={() =>
              void go(
                `Animate ${target}: ${anims.join(', ')}. Rig it first if it has no rig, author each clip with prism_model_rig so it loops where it should, add an Animator Controller with these clips and assign it, then show me.`,
              )
            }
          >
            <Icon name="sparkles" size={13} /> Make these animations
          </button>
        </div>
      )}

      {tab === 'prop' && (
        <div className="makers-body">
          <div className="makers-chips">
            {PROP_IDEAS.map((p) => (
              <button key={p} type="button" className={prop === p ? 'is-on' : ''} onClick={() => setProp(p)}>
                {p}
              </button>
            ))}
          </div>
          <input className="makers-idea" value={prop} onChange={(e) => setProp(e.target.value)} placeholder="Or describe your own prop…" />
          <button
            type="button"
            className="makers-go"
            disabled={busy || !prop.trim()}
            onClick={() =>
              void go(`Make a prop for my game: ${prop.trim()}. Build it as a model in models/props at a real-world size, give it good materials (use prism_image_generate for any printed texture), render it, and put it in the open scene.`)
            }
          >
            <Icon name="sparkles" size={13} /> Make this prop
          </button>
        </div>
      )}
    </div>
  );
}
